import React, { useState } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { CORPORATE_TOPICS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CorporateForm() {
    const [form, setForm] = useState({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        team_size: "",
        interests: [],
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));

    const toggleInterest = (topic) => {
        setForm((s) => ({
            ...s,
            interests: s.interests.includes(topic)
                ? s.interests.filter((t) => t !== topic)
                : [...s.interests, topic],
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (
            !form.company_name ||
            !form.contact_name ||
            !form.email ||
            !form.message
        ) {
            toast.error("Please fill in the required fields.");
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${API}/corporate`, form);
            toast.success(
                "Thank you. Your inquiry has been received — we'll be in touch within 1–2 working days.",
            );
            setForm({
                company_name: "",
                contact_name: "",
                email: "",
                phone: "",
                team_size: "",
                interests: [],
                message: "",
            });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={onSubmit}
            className="space-y-8"
            data-testid="corporate-form"
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">
                        Company Name
                    </Label>
                    <Input
                        value={form.company_name}
                        onChange={onChange("company_name")}
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="corp-input-company"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">
                        Contact Name
                    </Label>
                    <Input
                        value={form.contact_name}
                        onChange={onChange("contact_name")}
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="corp-input-contact"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">Email</Label>
                    <Input
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="corp-input-email"
                    />
                </div>
                <div className="space-y-2">
                    <Label className="ssh-eyebrow text-[#75736F]">Phone</Label>
                    <Input
                        value={form.phone}
                        onChange={onChange("phone")}
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="corp-input-phone"
                    />
                </div>
                <div className="space-y-2 sm:col-span-2">
                    <Label className="ssh-eyebrow text-[#75736F]">
                        Approximate Team Size
                    </Label>
                    <Input
                        value={form.team_size}
                        onChange={onChange("team_size")}
                        placeholder="e.g. 25, 100+"
                        className="bg-white border-[#EFEBE4] rounded-none h-12 focus-visible:ring-[#8A9A86]"
                        data-testid="corp-input-team-size"
                    />
                </div>
            </div>

            <div className="space-y-4">
                <Label className="ssh-eyebrow text-[#75736F]">
                    Areas of Interest
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {CORPORATE_TOPICS.map((topic) => (
                        <label
                            key={topic}
                            className="flex items-center gap-3 cursor-pointer text-sm text-[#2C2B29] hover:text-[#A68A6B] transition-colors"
                        >
                            <Checkbox
                                checked={form.interests.includes(topic)}
                                onCheckedChange={() => toggleInterest(topic)}
                                className="rounded-none border-[#A68A6B] data-[state=checked]:bg-[#A68A6B] data-[state=checked]:text-white"
                                data-testid={`corp-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            />
                            <span>{topic}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="space-y-2">
                <Label className="ssh-eyebrow text-[#75736F]">
                    Tell us about your team
                </Label>
                <Textarea
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Goals, format (workshop / circle / ongoing), timeline, anything specific you'd like to address."
                    className="bg-white border-[#EFEBE4] rounded-none min-h-[160px] focus-visible:ring-[#8A9A86]"
                    data-testid="corp-input-message"
                />
            </div>
            <Button
                type="submit"
                disabled={loading}
                className="ssh-btn-primary rounded-none px-10 py-6 text-xs uppercase tracking-[0.22em]"
                data-testid="corp-submit"
            >
                {loading ? "Sending..." : "Submit Inquiry"}
            </Button>
        </form>
    );
}
